import { Auth } from 'aws-amplify';
import dayjs from 'dayjs';

import { useOnlineStatus } from '../useOnlineStatus';
import { LAST_SYNC } from '../constants';
import { useDateFormatters } from './useDateFormatters';
import { TrackingEventType, useEventTracking } from './useEventTracking';

interface AIMSHeartbeatEvent {
  email: string;
  ipadOSVersion: string;
  serviceWorkerStatus: string;
  isOnline: boolean;
  lastUserInteractionTime: string | null;
  loginTime: string | null;
  logoutTime: string | null;
  lastSyncTime: string | null;
  comment: string | null;
}

const useAIMSHeartbeat = () => {
  const { trackEvent } = useEventTracking();
  const isOnline = useOnlineStatus();
  const { formatDateTime } = useDateFormatters();

  // Create event tracking for AIMS heartbeat
  const getiOSVersion = () => {
    const splits = navigator.userAgent.split('Version/');
    if (splits.length > 1) return splits[1].split(' ')[0];
    else return 'NA';
  };

  const getServerWorkerStatus = async () => {
    // determine service worker state
    try {
      const registrations = await navigator.serviceWorker?.getRegistrations();

      if (registrations?.length > 0) {
        const registration = registrations[0];

        if (registration.waiting) return 'waiting';
        else if (registration.installing) return 'installing';
        else if (registration.active) return 'active';
      }
    } catch (error) {
      console.log('🚀 ~ file: AIMSHeartbeat.tsx:35 ~ sendAIMSHeartbeat ~ error', error);
    }
    return 'NA';
  };

  const sendAIMSHeartbeat = async ({
    lastUserInteractionTime = dayjs(),
    loginTime,
    logoutTime,
    comment = {},
  }: {
    lastUserInteractionTime?: dayjs.Dayjs;
    loginTime?: dayjs.Dayjs;
    logoutTime?: dayjs.Dayjs;
    comment?: { [key: string]: string };
  }) => {
    const userDetails = await Auth.currentSession();
    const email = userDetails.getIdToken()?.payload?.email || 'NA';
    const serviceWorkerStatus = await getServerWorkerStatus();
    let lastSyncTime = null;
    const storedLastSync = localStorage.getItem(LAST_SYNC);
    if (storedLastSync) lastSyncTime = formatDateTime(JSON.parse(storedLastSync));

    const heartbeatEvent: AIMSHeartbeatEvent = {
      email,
      ipadOSVersion: getiOSVersion(),
      serviceWorkerStatus,
      isOnline,
      lastUserInteractionTime: formatDateTime(lastUserInteractionTime),
      loginTime: formatDateTime(loginTime),
      logoutTime: formatDateTime(logoutTime),
      lastSyncTime,
      comment: Object.keys(comment).length > 0 ? JSON.stringify(comment) : null,
    };

    await trackEvent(TrackingEventType.AIMS_HEART_BEAT, heartbeatEvent);
  };

  return { sendAIMSHeartbeat };
};

export { useAIMSHeartbeat };
